import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, type LinkProps } from "react-router";
import { ApiError } from "../lib/api.ts";
import { buttonClass, type Size, type Variant } from "./styles.ts";

// Primitives in the Fish Audio Semantic V2 language: pill buttons (h-10 / h-12), 2xl cards,
// status tones from --fish-v2-status-*.

export function Button({ variant = "primary", size = "md", className = "", type = "button", ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: Size }) {
  return <button type={type} className={buttonClass(variant, size, className)} {...rest} />;
}

export function LinkButton({ variant = "primary", size = "md", className = "", ...rest }: LinkProps & { variant?: Variant; size?: Size }) {
  return <Link className={buttonClass(variant, size, className)} {...rest} />;
}

export function Card({ children, className = "", ...rest }: { children: ReactNode; className?: string } & HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={`rounded-2xl border border-border bg-elevated p-4 sm:p-5 ${className}`} {...rest}>
      {children}
    </div>
  );
}

type Tone = "info" | "success" | "warning" | "danger" | "neutral";
const TONE: Record<Tone, string> = {
  info: "border-[var(--fish-v2-status-info-info-border)] bg-[var(--fish-v2-status-info-info-background)] text-[var(--fish-v2-status-info-info-foreground)]",
  success: "border-[var(--fish-v2-status-success-success-border)] bg-[var(--fish-v2-status-success-success-background)] text-[var(--fish-v2-status-success-success-foreground)]",
  warning: "border-[var(--fish-v2-status-warning-warning-border)] bg-[var(--fish-v2-status-warning-warning-background)] text-[var(--fish-v2-status-warning-warning-foreground)]",
  danger: "border-[var(--fish-v2-status-danger-error-border)] bg-[var(--fish-v2-status-danger-error-background)] text-[var(--fish-v2-status-danger-error-foreground)]",
  neutral: "border-border bg-fill text-body",
};

export function Alert({ tone = "info", title, children, className = "" }: { tone?: Tone; title?: ReactNode; children?: ReactNode; className?: string }) {
  return (
    <div role={tone === "danger" ? "alert" : "status"} className={`rounded-2xl border px-4 py-3 text-sm ${TONE[tone]} ${className}`}>
      {title ? <p className="font-medium">{title}</p> : null}
      {children ? <div className={title ? "mt-1" : ""}>{children}</div> : null}
    </div>
  );
}

/** Translate an API error code (errors.<code>), falling back to the server message. */
export function ErrorAlert({ error, className }: { error: unknown; className?: string }) {
  const { t } = useTranslation();
  if (!error) return null;
  const code = error instanceof ApiError ? error.code : "network_error";
  const msg = t(`errors.${code}`, { defaultValue: error instanceof Error && error.message ? error.message : t("errors.generic") });
  return (
    <Alert tone="danger" className={className}>
      {msg}
    </Alert>
  );
}

export function Spinner({ label }: { label?: string }) {
  const { t } = useTranslation();
  return (
    <div role="status" className="flex items-center justify-center gap-2 py-10 text-sm text-helper">
      <span className="size-4 animate-spin rounded-full border-2 border-fill-strong border-t-accent" aria-hidden />
      {label ?? t("common.loading")}
    </div>
  );
}

export function Badge({ children, tone = "neutral" }: { children: ReactNode; tone?: Tone }) {
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs ${TONE[tone]}`}>{children}</span>;
}

export function ProgressBar({ value, label }: { value: number; label?: string }) {
  const v = Math.max(0, Math.min(1, value));
  return (
    <div role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(v * 100)} className="h-1.5 w-full overflow-hidden rounded-full bg-fill">
      <div className="h-full rounded-full bg-accent transition-[width]" style={{ width: `${v * 100}%` }} />
    </div>
  );
}

export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-title">{label}</span>
      {children}
      {hint ? <span className="text-xs text-helper">{hint}</span> : null}
    </label>
  );
}
